import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Users } from "lucide-react";

const CURRENT_MEMBERS = 226;
const GOAL_MEMBERS = 284;

export const EntryTally = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, CURRENT_MEMBERS, {
      duration: 2.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      data-testid="entry-tally"
      className="mt-10 relative bg-slate-950 rounded-2xl px-6 py-8 sm:px-10 overflow-hidden border border-slate-800 shadow-[0_0_35px_rgba(0,240,255,0.15)]"
    >
      <div className="neon-hairline absolute top-0 left-0 right-0" />
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
        <div className="flex items-center gap-3 text-cyan-300">
          <Users size={30} className="shrink-0" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-left max-w-[190px] sm:max-w-none">
            Household members backing our school
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span
            data-testid="entry-tally-count"
            className="text-5xl sm:text-6xl font-black tabular-nums text-white"
            style={{ textShadow: "0 0 25px rgba(0,240,255,0.6)" }}
          >
            {value.toLocaleString()}
          </span>
          <span className="text-sm font-bold uppercase tracking-widest text-pink-400">
            of {GOAL_MEMBERS} needed
          </span>
        </div>
        <p className="text-[11px] font-semibold text-slate-400 sm:ml-2 text-center sm:text-left">
          Just {GOAL_MEMBERS - CURRENT_MEMBERS} to go — your membership matters!
        </p>
      </div>
      <div className="mt-6 h-2.5 rounded-full bg-slate-800 overflow-hidden max-w-2xl mx-auto">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${(CURRENT_MEMBERS / GOAL_MEMBERS) * 100}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="entry-tally-progress"
          className="h-full rounded-full bg-gradient-to-r from-pink-500 via-fuchsia-400 to-cyan-400"
          style={{ boxShadow: "0 0 14px rgba(0,240,255,0.5)" }}
        />
      </div>
    </motion.div>
  );
};
